import { Component, inject, Input, OnInit } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { Product, ApplicantProduct } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { firstValueFrom } from 'rxjs';

/** OAO checking and saving accounts component */
@Component({
  selector: 'ecp-oao-other-account',
  templateUrl: './oao-other-account.component.html'
})
export class OaoOtherAccountComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** oao Service objcet */
  @Input() public mainAccount: Product[];

  /** all membership services */
  @Input() public allProducts: Product[];

  /** co Applicant Services */
  @Input() public coApplicantProducts: ApplicantProduct[];

  /** Is Initial Loading from L360 Call */
  @Input() public isInitialLoading: boolean;

  /** Co Applicants Services : Local Variable */
  public coAppProducts: ApplicantProduct[];

  /** Storage ID */
  public arrayStorageId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares';

  /** showing checking account flag */
  public showAccounts = false;

  constructor(
    private readonly bundleHelperService: BundleHelperService,
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360Service: L360Service
  ) {
    this.coAppProducts = [];
  }

  public ngOnInit(): void {
    this.coAppProducts = this.coApplicantProducts.map((x) => Object.assign({}, x));
    // Set accordian Open status if any required field is active
    const isAnyShareOptOut = this.mainAccount.filter((f) => f.requiredOptInType.toLowerCase() === 'opt-out').length > 0 ? true : false;
    if (isAnyShareOptOut && this.isInitialLoading) {
      this.showAccounts = true;
    }
  }

  /** Account display toggle */
  public AccountsToggler(): void {
    this.showAccounts = !this.showAccounts;
  }

  /** get acocunt title */
  public get title(): string {
    return this.mainAccount.length > 0 ? this.mainAccount[0].typeName : '';
  }

  /** get account description */
  public get description(): string {
    return this.mainAccount.length > 0 ? this.mainAccount[0].description : '';
  }

  /** Saves the user selection to session storage */
  public primaryShareSelection(account: Product): void {
    // Clear Co-Applicants Shares. When remove primar applicant's share, then clear all the co applicant's share & services
    if (!account.isSelected) {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === account.id)
          .forEach((share) => {
            share.isSelected = false;
            share.isDisabled = false;
          });
      });
    } else {
      if (this.isYouthMembership()) {
        // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
        this.coAppProducts.forEach((applicant) => {
          applicant.shares
            .filter((f) => f.id === account.id)
            .forEach((share) => {
              share.isSelected = true;
              this.coAppShareSelection(share, applicant);
              share.isDisabled = true;
            });
        });
      }
    }

    this.UpdateBundleIndex();
    this.updateSelection();
  }

  /** getCurrentArraySize */
  public getCurrentArraySize(): number {
    return this.statefulService.arraySize(this.arrayStorageId);
  }

  // On Selection of Co Applicant's Share checkbox
  public coAppShareSelection(parentAccount: Product, coApplicantProduct: ApplicantProduct): void {
    const shareId = parentAccount.id;
    const isSelected = coApplicantProduct.shares.filter((f) => f.id === shareId)[0].isSelected;
    const rawReqBody = [];

    // Set Co Applicant Share Selection Status
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, parentAccount.bundleIndex, 'isSelected', isSelected));

    // Check whether max allowed applicant selection met or not, if so, then disable other shares
    const maxAllowedApplicant = parentAccount.numberOfJointApplicants;
    let selectedCoApp = 0;

    // Count Total Selected co Applicant Share
    this.coAppProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === shareId)
        .forEach((item) => {
          selectedCoApp = item.isSelected ? selectedCoApp + 1 : selectedCoApp;
        });
    });

    // If Max Allowed Co App selection equals to selected co app shares then disable remaing co applicant
    if (selectedCoApp === maxAllowedApplicant) {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = share.isSelected ? false : true;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
          });
      });
    } else {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = this.isYouthMembership() ? true : false;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
          });
      });
    }

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Generate Request Body */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public generateRequestBody(shareBundlePath: string, shareBundleIndex: number, propName: string, value: boolean): any {
    return {
      op: 'add',
      path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/${propName}`,
      value
    };
  }

  // Update isSelected property of Account/Optionsl Servies
  public UpdateBundleIndex(): void {
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
    });
  }

  /** Saves the user selection to session storage */
  public updateSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.allProducts);
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f == true).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /** check Joint Applicant availability. If youth membership, no need to check this condition.*/
  public hasJointApplicants(product: Product): boolean {
    if (this.isYouthMembership()) {
      return true;
    } else {
      return product.numberOfJointApplicants && product.numberOfJointApplicants > 0;
    }
  }
}
